import { setCookieContext, resetCookieContext, encodeContextObject, getPersistedContext } from './web-cookie.js';
import { addCtcReaderTreatments } from './web-ctc.js';
import { getNowUTC, getNMinutesFromNowUTC } from './web-datetime.js';
import { CONTEXT_CHANGE_EVENT, DATE_TIME_CHANGE_EVENT, VAT_INCLUSIVITY_QUERY_PARAMETER, DATE_TIME_QUERY_PARAMETER, COUPON_CHANGE_EVENT, VAT_INCLUSIVITY_CHANGE_EVENT, INITIALIZED_EVENT } from './shared-constants.js';
import { fireEvent } from './web-events.js';
import { getShopperInfo, isValidShopperInfo } from './web-shopper.js';
import { getVatInclusivity } from './web-vat.js';
import { getRemoteContext, applyCouponRemotely } from './web-pricing-context-service.js';
import { isSubtenantMandatory } from './pricing-context-module-helpers.js';

const getContextStringBlacklist = function() {
  return [
    "version",
    "vatInclusive",
    "expiresAt",
    "developmentMode",
    "shopperId"
  ];
};
const getEmptyContext = () => {
  return {
    version: 0,
    merchantId: void 0,
    market: void 0,
    couponCode: void 0,
    effectiveDateTime: void 0,
    customerGroups: void 0,
    vatInclusive: void 0,
    expiresAt: void 0,
    developmentMode: void 0,
    shopperId: void 0,
    subtenant: void 0
  };
};
const getCurrentContext = (filterOutNullAndEmpties = false, filterOutBlacklist = false) => {
  const persistedContext = getPersistedContext();
  if (persistedContext === null) {
    throw new Error("Pricing context module not initialized.");
  }
  let context = persistedContext;
  try {
    if (filterOutNullAndEmpties) {
      const contextAsAny = context;
      const filteredContext = Object.keys(contextAsAny).filter((k) => contextAsAny[k] !== void 0).filter((k) => contextAsAny[k] !== "").reduce((filtered, field) => {
        filtered[field] = contextAsAny[field];
        return filtered;
      }, { version: 0 });
      context = filteredContext;
    }
    if (filterOutBlacklist) {
      const contextAsAny = context;
      const blacklist = getContextStringBlacklist();
      const filteredContext = Object.keys(contextAsAny).filter((key) => !blacklist.includes(key)).reduce((filtered, field) => {
        filtered[field] = contextAsAny[field];
        return filtered;
      }, { version: 0 });
      context = filteredContext;
    }
    context.customerGroups = addCtcReaderTreatments(persistedContext.customerGroups);
    return context;
  } catch (ex) {
    throw new Error("Failed to decode pricing context string.");
  }
};
const getContextString = function() {
  return encodeContextObject(getCurrentContext(true, true));
};
const clearDataOnOrderPlaced = function() {
  const cachedContext = getCurrentContext();
  cachedContext.couponCode = void 0;
  cachedContext.expiresAt = getNMinutesFromNowUTC(5);
  setCookieContext(cachedContext);
};
const getContextQueryString = (encodeUri = false) => {
  const context = getCurrentContext(true, true);
  const definedContextFields = Object.keys(context);
  const contextAsAny = context;
  let queryString = definedContextFields.filter((key) => contextAsAny[key] !== void 0).filter((key) => !Array.isArray(contextAsAny[key])).map((key) => `${key}=${contextAsAny[key]}`).join("&");
  const arrayFieldKeys = definedContextFields.filter((key) => contextAsAny[key] !== void 0).filter((key) => Array.isArray(contextAsAny[key]));
  for (let i = 0; i < arrayFieldKeys.length; i += 1) {
    const arrayFieldKey = arrayFieldKeys[i];
    const arrayFieldQueryString = contextAsAny[arrayFieldKey].map((val) => `${arrayFieldKey}=${val}`).join("&");
    queryString += `&${arrayFieldQueryString}`;
  }
  return encodeUri ? encodeURI(queryString) : queryString;
};
const couponWasApplied = function(context, couponCode) {
  if (!context.couponCode) {
    return !couponCode;
  }
  return context.couponCode.toUpperCase() === couponCode?.toUpperCase();
};
const setCoupon = async function({ couponCode }) {
  const context = getCurrentContext();
  if (!context.couponCode || context.couponCode.toUpperCase() !== couponCode?.toUpperCase()) {
    try {
      const shopperInfo = getShopperInfo();
      if (!isValidShopperInfo(shopperInfo)) {
        throw new Error("Missing shopper info");
      }
      const request = {
        merchantId: context.merchantId,
        market: context.market,
        shopperInfo,
        couponCode,
        developmentMode: !!context.developmentMode,
        subtenant: context.subtenant
      };
      const couponAppliedResponse = await applyCouponRemotely(request);
      const nextContext = {
        ...couponAppliedResponse.currentPricingContext,
        vatInclusive: context.vatInclusive,
        shopperId: shopperInfo.shopperId
      };
      setCookieContext(nextContext);
      fireEvent(COUPON_CHANGE_EVENT, nextContext);
      fireEvent(CONTEXT_CHANGE_EVENT, nextContext);
      return {
        couponApplied: couponWasApplied(nextContext, couponCode),
        validationErrors: couponAppliedResponse.validationErrors || [],
        couponInfoLinks: couponAppliedResponse.couponInfoLinks || {},
        currentPricingContext: nextContext
      };
    } catch (err) {
      console.info("Retaining existing context");
    }
  }
  return {
    couponApplied: couponWasApplied(context, couponCode),
    validationErrors: ["UnableToValidate"],
    couponInfoLinks: {},
    currentPricingContext: context
  };
};
const setEffectiveDateTime = async function(e) {
  if (typeof e !== "string" && !(e instanceof String)) {
    throw new Error("Must pass a string for parameter 'effectiveDateTime'");
  }
  const effectiveDateTime = e.toString();
  const cachedContext = getCurrentContext();
  if (effectiveDateTime === cachedContext.effectiveDateTime) {
    return;
  }
  try {
    const shopperInfo = getShopperInfo();
    if (!isValidShopperInfo(shopperInfo)) {
      throw new Error("Missing shopper info to call for remote context");
    }
    const remoteContext = await getRemoteContext(
      {
        merchantId: cachedContext.merchantId,
        market: cachedContext.market,
        shopperInfo,
        effectiveDateTime,
        developmentMode: !!cachedContext.developmentMode,
        subtenant: cachedContext.subtenant
      }
    );
    const nextContext = {
      ...remoteContext,
      vatInclusive: cachedContext.vatInclusive,
      shopperId: shopperInfo.shopperId
    };
    setCookieContext(nextContext);
    fireEvent(CONTEXT_CHANGE_EVENT, nextContext);
    if (nextContext.effectiveDateTime && nextContext.effectiveDateTime !== cachedContext.effectiveDateTime) {
      fireEvent(DATE_TIME_CHANGE_EVENT, nextContext);
    }
  } catch (error) {
    console.info("Retaining existing context");
  }
};
const setVatInclusivity = function(vatInclusive) {
  const context = getCurrentContext();
  context.vatInclusive = vatInclusive;
  setCookieContext(context);
  fireEvent(VAT_INCLUSIVITY_CHANGE_EVENT, context);
  fireEvent(CONTEXT_CHANGE_EVENT, context);
};
const scrapeURLParams = function() {
  const queryParams = {};
  if (typeof window === "undefined" || typeof window.location === "undefined" || !window.location.search) {
    return queryParams;
  }
  const urlParams = new URLSearchParams(window.location.search.toUpperCase());
  const vatInclusivityQueryParamString = urlParams.get(VAT_INCLUSIVITY_QUERY_PARAMETER);
  if (vatInclusivityQueryParamString) {
    queryParams.vatInclusive = vatInclusivityQueryParamString === "TRUE";
  }
  const dateTimeQueryParamString = urlParams.get(DATE_TIME_QUERY_PARAMETER);
  if (dateTimeQueryParamString) {
    queryParams.effectiveDateTime = dateTimeQueryParamString;
  }
  return queryParams;
};
const backgroundRefresh = async () => {
  const queryParams = scrapeURLParams();
  let initialCachedContext;
  try {
    initialCachedContext = getCurrentContext();
  } catch (ex) {
    return;
  }
  const shopperInfo = getShopperInfo();
  const shopperId = shopperInfo && shopperInfo.shopperId ? shopperInfo.shopperId : "";
  if (!isValidShopperInfo(shopperInfo) || initialCachedContext === void 0 || initialCachedContext.merchantId === void 0 || initialCachedContext.market === void 0 || isSubtenantMandatory(initialCachedContext.merchantId) && initialCachedContext.subtenant === void 0) {
    return;
  }
  let newContext;
  try {
    newContext = await getRemoteContext(
      {
        merchantId: initialCachedContext.merchantId,
        market: initialCachedContext.market,
        shopperInfo,
        effectiveDateTime: queryParams.effectiveDateTime,
        developmentMode: !!initialCachedContext.developmentMode,
        subtenant: initialCachedContext.subtenant
      }
    );
  } catch (error) {
    return;
  }
  newContext.customerGroups = addCtcReaderTreatments(newContext.customerGroups);
  newContext.vatInclusive = await getVatInclusivity(initialCachedContext, newContext, queryParams);
  newContext.shopperId = shopperId;
  let latestCachedContext;
  try {
    latestCachedContext = getCurrentContext();
  } catch (ex) {
    return;
  }
  if (initialCachedContext.merchantId !== latestCachedContext.merchantId || initialCachedContext.market !== latestCachedContext.market) {
    return;
  }
  setCookieContext(newContext);
  fireEvent(CONTEXT_CHANGE_EVENT, newContext);
};
const isInitialized = (merchantId, market, isDevelopmentMode, subtenant) => {
  const shopperInfo = getShopperInfo();
  const queryParams = scrapeURLParams();
  let cachedContext;
  try {
    cachedContext = getCurrentContext();
  } catch (ex) {
    cachedContext = getEmptyContext();
  }
  if (merchantId && market && isDevelopmentMode !== void 0) {
    const v3ContextInitialized = cachedContext.shopperId !== void 0 && // You have (A) a context from a previous version - or - (B) a context from a session where auth failed to load
    (!shopperInfo.shopperId || shopperInfo.shopperId === cachedContext.shopperId) && // Check cached shopperId against current shopperId, if available
    cachedContext.developmentMode === isDevelopmentMode && // You are in the wrong env
    cachedContext.expiresAt !== void 0 && // You have a context from a previous version
    cachedContext.expiresAt.toISOString() > getNowUTC() && // You have an expired context
    cachedContext.merchantId === merchantId.toUpperCase() && // You have a context from the wrong vendor's website
    cachedContext.market === market.toUpperCase() && // You have a context from the wrong website for the vendor
    (!queryParams.effectiveDateTime || cachedContext.effectiveDateTime === queryParams.effectiveDateTime) && // You are attempting to re-init from a session with a different effectivedatetime
    (!isSubtenantMandatory(cachedContext.merchantId) || cachedContext.subtenant === subtenant);
    return v3ContextInitialized;
  }
  return !(cachedContext.merchantId === void 0 || cachedContext.market === void 0 || cachedContext.vatInclusive === void 0 || isSubtenantMandatory(cachedContext.merchantId) && cachedContext.subtenant === void 0);
};
async function init(merchantId, market, isDevelopmentMode = false, subtenant) {
  if (!(typeof merchantId === "string" || merchantId instanceof String) || merchantId === "" || merchantId.toLowerCase() === "undefined") {
    throw new Error("Must pass a non-empty, defined string for parameter 'merchantId'");
  }
  const merchantIdAsString = merchantId.toString();
  if (!(typeof market === "string" || market instanceof String) || market === "" || market.toLowerCase() === "undefined") {
    throw new Error("Must pass a non-empty, defined string for parameter 'market'");
  }
  const marketAsString = market.toString();
  if (isSubtenantMandatory(merchantId) && (!(typeof subtenant === "string" || subtenant instanceof String) || subtenant === "" || subtenant.toLowerCase() === "undefined")) {
    throw new Error(`Must pass a non-empty, defined string for parameter 'subtenant' for merchantId ${merchantId}`);
  }
  const subtenantAsString = subtenant?.toString();
  const queryParams = scrapeURLParams();
  let cachedContext;
  try {
    cachedContext = getCurrentContext();
  } catch (ex) {
    cachedContext = getEmptyContext();
  }
  const shopperInfo = getShopperInfo();
  const shopperId = shopperInfo && shopperInfo.shopperId ? shopperInfo.shopperId : "";
  const contextDataNeedsRefresh = cachedContext.developmentMode !== isDevelopmentMode || // You are in the wrong env
  cachedContext.expiresAt === void 0 || // You have a context from a previous version
  cachedContext.expiresAt.toISOString() <= getNowUTC() || // You have an expired context
  cachedContext.merchantId !== merchantIdAsString.toUpperCase() || // You have a context from the wrong vendor's website
  cachedContext.market !== marketAsString.toUpperCase() || // You have a context from the wrong website for the vendor
  queryParams.effectiveDateTime && cachedContext.effectiveDateTime !== queryParams.effectiveDateTime || // You are attempting to re-init from a session with a different effectivedatetime
  isSubtenantMandatory(merchantId) && cachedContext.subtenant !== subtenantAsString;
  const contextOwnershipNeedsRefresh = shopperId !== "" && cachedContext.shopperId !== shopperId;
  let newContext;
  if (contextDataNeedsRefresh || contextOwnershipNeedsRefresh) {
    try {
      if (!isValidShopperInfo(shopperInfo)) {
        throw new Error("Missing shopper info to call for remote context");
      }
      newContext = await getRemoteContext(
        {
          merchantId: merchantIdAsString,
          market: marketAsString,
          shopperInfo,
          effectiveDateTime: queryParams.effectiveDateTime,
          developmentMode: isDevelopmentMode,
          subtenant: subtenantAsString
        }
      );
    } catch (error) {
      newContext = cachedContext;
      newContext.merchantId = merchantId.toUpperCase();
      newContext.market = market.toUpperCase();
      newContext.expiresAt = getNMinutesFromNowUTC(1);
      newContext.developmentMode = isDevelopmentMode;
      newContext.subtenant = subtenantAsString;
    }
  } else {
    newContext = cachedContext;
  }
  newContext.customerGroups = addCtcReaderTreatments(newContext.customerGroups);
  newContext.vatInclusive = await getVatInclusivity(cachedContext, newContext, queryParams);
  newContext.shopperId = shopperId;
  setCookieContext(newContext);
  fireEvent(INITIALIZED_EVENT, newContext);
  return newContext;
}
const reset = () => {
  resetCookieContext();
};

export { backgroundRefresh, clearDataOnOrderPlaced, getContextQueryString, getContextString, getCurrentContext, init, isInitialized, reset, setCoupon, setEffectiveDateTime, setVatInclusivity };
//# sourceMappingURL=web-context.js.map
